import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { tasksApi, workflowsApi, usersApi, extractOne } from '../../api/facades';

const inputClass = 'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const TaskCreateForWorkflowPage: React.FC = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const wfId = workflowId ? parseInt(workflowId, 10) : NaN;

  const [task_subject, setTaskSubject] = useState('');
  const [order, setOrder] = useState<number | ''>('');
  const [depends_on_task_id, setDependsOnTaskId] = useState<number | ''>('');
  const [project_status, setProjectStatus] = useState('Pending');
  const [priority, setPriority] = useState('Medium');
  const [start_date, setStartDate] = useState('');
  const [end_date, setEndDate] = useState('');
  const [description, setDescription] = useState('');
  const [user_ids, setUserIds] = useState<number[]>([]);

  const { data: workflowRes } = useQuery({
    queryKey: ['workflows', wfId],
    queryFn: () => workflowsApi.get(wfId),
    enabled: !Number.isNaN(wfId),
  });
  const workflow = extractOne(workflowRes);
  const workflowTasks = (workflow as any)?.tasks ?? [];

  const { data: usersRes } = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list({ per_page: 100 }) });
  const users = useMemo(() => {
    const d = usersRes?.data?.data;
    return Array.isArray(d?.data) ? d.data : Array.isArray(d) ? d : [];
  }, [usersRes]);

  const create = useMutation({
    mutationFn: () =>
      tasksApi.create({
        workflow_id: wfId,
        order: order === '' ? undefined : order,
        depends_on_task_id: depends_on_task_id === '' ? null : depends_on_task_id,
        task_subject: task_subject.trim(),
        project_status: project_status || null,
        priority: priority || null,
        start_date: start_date || null,
        end_date: end_date || null,
        description: description.trim() || null,
        user_ids: user_ids.length ? user_ids : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows', wfId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Task created.');
      navigate(`/dashboard/workflows/${wfId}`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || e?.message || 'Create failed.'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!task_subject.trim()) return;
    create.mutate();
  };

  const toggleUser = (id: number) => {
    setUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  if (Number.isNaN(wfId)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <p className="text-slate-600">Invalid workflow.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/dashboard/workflows')}>Back</Button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">Add task to workflow</h1>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
        <FormField label="Task name" hint="Required">
          <input className={inputClass} value={task_subject} onChange={(e) => setTaskSubject(e.target.value)} required placeholder="e.g. Review document" />
        </FormField>
        <FormField label="Order">
          <input className={inputClass} type="number" min={0} value={order === '' ? '' : order} onChange={(e) => setOrder(e.target.value === '' ? '' : parseInt(e.target.value, 10))} placeholder="Optional" />
        </FormField>
        <FormField label="Depends on task">
          <select className={inputClass} value={depends_on_task_id === '' ? '' : depends_on_task_id} onChange={(e) => setDependsOnTaskId(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">— None —</option>
            {workflowTasks.map((t: any) => (
              <option key={t.id} value={t.id}>{t.task_subject} (#{t.id})</option>
            ))}
          </select>
        </FormField>
        <FormField label="Status">
          <select className={inputClass} value={project_status} onChange={(e) => setProjectStatus(e.target.value)}>
            <option value="Pending">Pending</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
          </select>
        </FormField>
        <FormField label="Priority">
          <select className={inputClass} value={priority} onChange={(e) => setPriority(e.target.value)}>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
          </select>
        </FormField>
        <FormField label="Start date">
          <input className={inputClass} type="date" value={start_date} onChange={(e) => setStartDate(e.target.value)} />
        </FormField>
        <FormField label="End date">
          <input className={inputClass} type="date" value={end_date} onChange={(e) => setEndDate(e.target.value)} />
        </FormField>
        <div className="md:col-span-2">
          <FormField label="Assignees">
            <div className="flex flex-wrap gap-2">
              {users.map((u: any) => (
                <label key={u.id} className="inline-flex items-center gap-1 text-sm">
                  <input type="checkbox" checked={user_ids.includes(u.id)} onChange={() => toggleUser(u.id)} />
                  <span>{u.name ?? u.email ?? u.id}</span>
                </label>
              ))}
            </div>
          </FormField>
        </div>
        <div className="md:col-span-2">
          <FormField label="Description">
            <textarea className={inputClass} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </FormField>
        </div>
        <div className="md:col-span-2 flex gap-2">
          <Button variant="secondary" type="button" onClick={() => navigate(`/dashboard/workflows/${wfId}`)}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!task_subject.trim() || create.isPending}>{create.isPending ? 'Creating…' : 'Create task'}</Button>
        </div>
      </form>
    </div>
  );
};

export default TaskCreateForWorkflowPage;
